/**
 * Sprint 2 CP3.5 (2026-05-16) — Detecção de padrão do loop pra painel de debug.
 *
 * O backend (CP1 do refactor 2026-05-16) emite N cópias de uma `paddedSequence`
 * dentro da janela horária — então a playlist resultante é exatamente o
 * padrão se repetindo N vezes. Esse helper detecta o padrão a partir da
 * playlist final, sem precisar de campo novo no DTO.
 *
 * Mesma lógica usada no admin (`PlaybackLoopTab.tsx` CP2) — mantida em
 * sincronia. Comportamento canônico em LOOP_REPRODUCAO_PLAYER.md, governado
 * por R1-R8 em .claude/rules/loop-reproducao-player.md.
 */

/** Item mínimo necessário pra detecção — id é o discriminador. */
export interface LoopPatternItem {
  /** Identificador único do conteúdo. Usado pra comparar items entre posições. */
  id: string;
  /** Duração em segundos. */
  duration: number;
  /** Opcional — id da campanha origem (pra agrupar contagens por campanha). */
  campanhaId?: number | null;
  /** Opcional — nome legível da campanha (pra exibir no painel). */
  campanhaName?: string;
}

/** Resultado da detecção do padrão. */
export interface LoopPatternInfo {
  /** Tamanho do menor padrão repetitivo (em items). 0 se vazio. */
  patternLength: number;
  /** Quantas vezes o padrão se repete na sequência total. ≥1. */
  repetitions: number;
  /** Duração total de 1 padrão em segundos. */
  patternDurationS: number;
  /** Tamanho total da sequência (items.length). */
  totalLength: number;
  /** Duração total da sequência (= patternDurationS × repetitions). */
  totalDurationS: number;
}

/** Contagem agregada por campanha pra exibir no painel. */
export interface CampaignLoopStats {
  campanhaId: number;
  campanhaName?: string;
  /** Quantas vezes a campanha aparece em 1 padrão. */
  countPerLoop: number;
  /** Quantas vezes a campanha aparece na hora inteira (= countPerLoop × repetitions). */
  countTotal: number;
}

/**
 * Detecta o menor padrão repetitivo na lista de items. Procura o menor `p`
 * tal que `p` divide `items.length` perfeitamente E `items[i].id === items[i+p].id`
 * pra todo `i` válido.
 *
 * Fallback gracioso: quando não há padrão claro (sequência sem repetição
 * detectável), retorna a lista inteira como 1 padrão de 1 repetição.
 *
 * Complexidade: O(N²/4) no pior caso — aceitável até N ~ alguns milhares.
 */
export function detectLoopPattern(items: LoopPatternItem[]): LoopPatternInfo {
  if (items.length === 0) {
    return {
      patternLength: 0,
      repetitions: 0,
      patternDurationS: 0,
      totalLength: 0,
      totalDurationS: 0,
    };
  }

  if (items.length === 1) {
    const dur = items[0].duration;
    return {
      patternLength: 1,
      repetitions: 1,
      patternDurationS: dur,
      totalLength: 1,
      totalDurationS: dur,
    };
  }

  for (let p = 1; p <= Math.floor(items.length / 2); p++) {
    if (items.length % p !== 0) continue;
    let matches = true;
    for (let i = 0; i + p < items.length; i++) {
      if (items[i].id !== items[i + p].id) {
        matches = false;
        break;
      }
    }
    if (matches) {
      const patternDurationS = items
        .slice(0, p)
        .reduce((s, it) => s + it.duration, 0);
      return {
        patternLength: p,
        repetitions: items.length / p,
        patternDurationS,
        totalLength: items.length,
        totalDurationS: patternDurationS * (items.length / p),
      };
    }
  }

  const totalDurationS = items.reduce((s, it) => s + it.duration, 0);
  return {
    patternLength: items.length,
    repetitions: 1,
    patternDurationS: totalDurationS,
    totalLength: items.length,
    totalDurationS,
  };
}

/**
 * Calcula a posição atual no padrão (1-based) e a iteração atual do loop.
 * Útil pra exibir "Mídia: 3/23 no padrão · Loop 1/12 da hora".
 */
export function computeLoopPosition(
  currentIndex: number,
  pattern: LoopPatternInfo,
): { positionInPattern: number; currentLoopIter: number } {
  if (pattern.patternLength === 0) {
    return { positionInPattern: 0, currentLoopIter: 0 };
  }
  const slotInLoop = currentIndex % pattern.patternLength;
  const loopIter = Math.floor(currentIndex / pattern.patternLength);
  return {
    positionInPattern: slotInLoop + 1, // 1-based pra UI
    currentLoopIter: loopIter + 1, // 1-based pra UI
  };
}

/**
 * Agrega contagens por campanha pra exibir no painel de debug do player.
 *
 * Ordem do resultado: primeiro as campanhas com MAIS impressões por loop
 * (decrescente) — destaca as que têm `min_frequency` alto (provavelmente
 * contratuais com mais peso).
 */
export function computeCampaignStats(
  items: LoopPatternItem[],
  pattern: LoopPatternInfo,
): CampaignLoopStats[] {
  if (items.length === 0 || pattern.patternLength === 0) return [];

  // Conta o padrão (primeiro N items) pra ter countPerLoop preciso
  const counts = new Map<number, { name?: string; perLoop: number }>();
  for (let i = 0; i < pattern.patternLength; i++) {
    const it = items[i];
    if (it.campanhaId == null) continue;
    const entry = counts.get(it.campanhaId);
    if (entry) {
      entry.perLoop += 1;
      if (!entry.name && it.campanhaName) entry.name = it.campanhaName;
    } else {
      counts.set(it.campanhaId, {
        name: it.campanhaName,
        perLoop: 1,
      });
    }
  }

  const result: CampaignLoopStats[] = [];
  counts.forEach((value, campanhaId) => {
    result.push({
      campanhaId,
      campanhaName: value.name,
      countPerLoop: value.perLoop,
      countTotal: value.perLoop * pattern.repetitions,
    });
  });

  // Ordena: maior contagem por loop primeiro (geralmente são as com mais peso)
  result.sort((a, b) => b.countPerLoop - a.countPerLoop);
  return result;
}
